/**
 * Input Validation Utilities
 * Provides profanity filtering and fuzzy job role matching
 */

// Common profanity words to filter
const PROFANITY_LIST = [
  'fuck', 'shit', 'bitch', 'asshole', 'damn', 'hell', 'crap', 'bastard',
  'dick', 'pussy', 'cock', 'cunt', 'motherfucker', 'fck', 'wtf', 'stfu',
  'bullshit', 'piss', 'slut', 'whore', 'fag', 'retard', 'nigger', 'nigga',
  'ass', 'douche', 'jackass', 'dumbass', 'douchebag', 'moron', 'idiot'
];

// Common job roles for fuzzy matching
const COMMON_JOB_ROLES = [
  'Software Engineer', 'Software Developer', 'Full Stack Developer', 'Frontend Developer', 
  'Backend Developer', 'Web Developer', 'Mobile Developer', 'DevOps Engineer',
  'Data Scientist', 'Data Analyst', 'Data Engineer', 'Machine Learning Engineer',
  'Product Manager', 'Project Manager', 'Program Manager', 'Scrum Master',
  'Marketing Manager', 'Digital Marketing Manager', 'Content Marketing Manager',
  'Sales Manager', 'Account Manager', 'Business Development Manager',
  'UX Designer', 'UI Designer', 'Product Designer', 'Graphic Designer',
  'Financial Analyst', 'Business Analyst', 'Systems Analyst',
  'HR Manager', 'Recruiter', 'Talent Acquisition Specialist',
  'Customer Success Manager', 'Customer Support Specialist',
  'Operations Manager', 'Supply Chain Manager', 'Quality Assurance Engineer',
  'Network Engineer', 'Security Engineer', 'Cloud Engineer', 'Database Administrator',
  'Content Writer', 'Technical Writer', 'Copywriter',
  'Accountant', 'Controller', 'CFO', 'CTO', 'CEO', 'COO',
  'Nurse', 'Pharmacist', 'Physical Therapist', 'Medical Assistant',
  'Teacher', 'Professor', 'Instructor', 'Training Specialist',
  'Consultant', 'Strategy Consultant', 'Management Consultant',
  'Real Estate Agent', 'Insurance Agent', 'Legal Assistant', 'Paralegal',
  'Chef', 'Restaurant Manager', 'Retail Manager', 'Store Manager'
];

/**
 * Check if text contains profanity
 * @param {string} text - Text to check
 * @returns {boolean} - True if profanity detected
 */
export function containsProfanity(text) {
  if (!text || typeof text !== 'string') return false;
  
  const lowerText = text.toLowerCase();
  
  // Check for exact matches and variations
  for (const word of PROFANITY_LIST) {
    // Check for whole word matches with word boundaries
    const regex = new RegExp(`\\b${word}\\b|${word.split('').join('[*@#$!\\s]*')}`, 'i');
    if (regex.test(lowerText)) {
      return true;
    }
    
    // Check for leetspeak variations (e.g., sh1t, f*ck, a$$)
    const leetspeak = word
      .replace(/a/g, '[a@4]')
      .replace(/e/g, '[e3]')
      .replace(/i/g, '[i1!]')
      .replace(/o/g, '[o0]')
      .replace(/s/g, '[s$5]')
      .replace(/t/g, '[t7]');
    
    const leetspeakRegex = new RegExp(`\\b${leetspeak}\\b`, 'i');
    if (leetspeakRegex.test(lowerText)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 
 * @param {string} str2 
 * @returns {number} - Edit distance
 */
function levenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1.charAt(i - 1) === str2.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
}

/**
 * Calculate similarity score between two strings (0-1)
 * @param {string} str1 
 * @param {string} str2 
 * @returns {number} - Similarity score
 */
function similarityScore(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const distance = levenshteinDistance(longer.toLowerCase(), shorter.toLowerCase());
  return (longer.length - distance) / longer.length;
}

/**
 * Find closest matching job role from common roles
 * @param {string} input - User input job role
 * @returns {object} - { original, suggested, similarity, shouldCorrect }
 */
export function fuzzyMatchJobRole(input) {
  if (!input || typeof input !== 'string') {
    return { original: input, suggested: null, similarity: 0, shouldCorrect: false };
  }

  const trimmedInput = input.trim();
  
  // If input is very short (< 3 chars), don't suggest
  if (trimmedInput.length < 3) {
    return { original: trimmedInput, suggested: null, similarity: 0, shouldCorrect: false };
  }

  let bestMatch = null;
  let bestScore = 0;

  for (const role of COMMON_JOB_ROLES) {
    const score = similarityScore(trimmedInput, role);
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = role;
    }
  }

  // Suggest correction if similarity is between 0.6 and 0.95
  // (0.95+ means it's already very close, < 0.6 means too different)
  const shouldCorrect = bestScore >= 0.6 && bestScore < 0.95;

  return {
    original: trimmedInput,
    suggested: shouldCorrect ? bestMatch : null,
    similarity: bestScore,
    shouldCorrect
  };
}

/**
 * Validate job role input - checks for profanity and suggests corrections
 * @param {string} input - Job role input
 * @returns {object} - { valid, error, suggestion }
 */
export function validateJobRole(input) {
  if (!input || !input.trim()) {
    return {
      valid: false,
      error: 'Please enter a job role.',
      suggestion: null
    };
  }

  // Check for profanity
  if (containsProfanity(input)) {
    return {
      valid: false,
      error: 'Please enter a professional job role without inappropriate language.',
      suggestion: null
    };
  }

  // Check length
  if (input.trim().length < 2) {
    return {
      valid: false,
      error: 'Job role must be at least 2 characters long.',
      suggestion: null
    };
  }

  if (input.trim().length > 100) {
    return {
      valid: false,
      error: 'Job role must be less than 100 characters.',
      suggestion: null
    };
  }

  // Check for fuzzy match
  const fuzzyMatch = fuzzyMatchJobRole(input);

  return {
    valid: true,
    error: null,
    suggestion: fuzzyMatch.shouldCorrect ? fuzzyMatch.suggested : null
  };
}

/**
 * Validate user response - checks for profanity and minimum length
 * @param {string} response - User response text
 * @param {number} minLength - Minimum length required (default 10)
 * @returns {object} - { valid, error }
 */
export function validateUserResponse(response, minLength = 10) {
  if (!response || !response.trim()) {
    return {
      valid: false,
      error: 'Please enter your response before continuing.'
    };
  }

  // Check for profanity
  if (containsProfanity(response)) {
    return {
      valid: false,
      error: 'Please provide a professional response without inappropriate language.'
    };
  }

  // Check minimum length
  if (response.trim().length < minLength) {
    return {
      valid: false,
      error: `Please provide a more detailed response (at least ${minLength} characters).`
    };
  }

  return {
    valid: true,
    error: null
  };
}

/**
 * Clean profanity from text by replacing with asterisks
 * @param {string} text - Text to clean
 * @returns {string} - Cleaned text
 */
export function cleanProfanity(text) {
  if (!text || typeof text !== 'string') return text;
  
  let cleanedText = text;
  
  for (const word of PROFANITY_LIST) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    cleanedText = cleanedText.replace(regex, (match) => {
      return '*'.repeat(match.length);
    });
  }
  
  return cleanedText;
}

